import { forwardRef } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { isFragmentRef } from "@skydio/relay-toolkit";
import { Tag } from "@skydio/rivet-ui";

import { DeviceHealthStatusTag } from "./DeviceHealthStatusTag";

import type { ComponentPropsWithoutRef } from "react";
import type { UnpackedFragmentData } from "@skydio/relay-toolkit";
import type { ForwardRefComponentWithSubcomponents } from "@skydio/rivet-ui";
import type {
  DeviceConnectivityHealthTag_device$data,
  DeviceConnectivityHealthTag_device$key,
} from "../../__generated__/DeviceConnectivityHealthTag_device.graphql";

const deviceFragment = graphql`
  fragment DeviceConnectivityHealthTag_device on Device {
    id
    isOnline
    deviceHealthStatus
    ...DeviceHealthStatusTag_device
  }
`;

type BaseProps = ComponentPropsWithoutRef<"div">;

export type DeviceConnectivityHealthTagFragment = DeviceConnectivityHealthTag_device$key;
export type DeviceConnectivityHealthTagData =
  UnpackedFragmentData<DeviceConnectivityHealthTag_device$data>;

interface InnerProps extends BaseProps {
  deviceData: DeviceConnectivityHealthTagData;
}

const DeviceConnectivityHealthTagInner = forwardRef<HTMLDivElement, InnerProps>(
  ({ deviceData, ...props }, ref) => {
    const connectivityTag = deviceData.isOnline ? (
      <Tag color="success" size="sm">
        Online
      </Tag>
    ) : (
      <Tag color="neutral" size="sm">
        Offline
      </Tag>
    );

    return (
      <div className="flex flex-col gap-1" ref={ref} {...props}>
        <div>{connectivityTag}</div>
        {deviceData.deviceHealthStatus !== "GREEN" &&
          deviceData.deviceHealthStatus !== "UNKNOWN" && (
            <div>
              <DeviceHealthStatusTag device={deviceData} />
            </div>
          )}
      </div>
    );
  }
);
DeviceConnectivityHealthTagInner.displayName = "DeviceConnectivityHealthTag.Inner";

interface WrapperProps extends BaseProps {
  deviceRef: DeviceConnectivityHealthTagFragment;
}

const DeviceConnectivityHealthTagWrapper = forwardRef<HTMLDivElement, WrapperProps>(
  ({ deviceRef, ...props }, ref) => {
    const deviceData = useFragment(deviceFragment, deviceRef);
    return <DeviceConnectivityHealthTagInner deviceData={deviceData} {...props} ref={ref} />;
  }
);
DeviceConnectivityHealthTagWrapper.displayName = "DeviceConnectivityHealthTag.Wrapper";

export type DeviceConnectivityHealthTagProps = BaseProps & {
  device: DeviceConnectivityHealthTagData | DeviceConnectivityHealthTagFragment;
};

export const DeviceConnectivityHealthTag = forwardRef<
  HTMLDivElement,
  DeviceConnectivityHealthTagProps
>(({ device, ...props }, ref) => {
  if (isFragmentRef(device, "DeviceConnectivityHealthTag_device")) {
    return <DeviceConnectivityHealthTagWrapper deviceRef={device} {...props} ref={ref} />;
  }
  return <DeviceConnectivityHealthTagInner deviceData={device} {...props} ref={ref} />;
}) as ForwardRefComponentWithSubcomponents<
  HTMLDivElement,
  {
    Inner: typeof DeviceConnectivityHealthTagInner;
    Wrapper: typeof DeviceConnectivityHealthTagWrapper;
  },
  DeviceConnectivityHealthTagProps
>;
DeviceConnectivityHealthTag.displayName = "DeviceConnectivityHealthTag";
